import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import UnbeatableSection from '../../Components/UnbeatableSection'
import OurServiceSection from '../../Components/OurServiceSection'
import './styles.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import Input from '../../Components/Input'
import InputArea from '../../Components/InputArea'
import CustomButton from '../../Components/CustomButton'
import SlideShow from '../../Components/SlideShow'

import slide1 from '../../Assets/Images/slide_1.jpeg'
import slide2 from '../../Assets/Images/slide_2.jpeg'
import slide3 from '../../Assets/Images/slide_3.jpeg'
import AccordionGroup from '../../Components/AccordionGroup'
import { SERVICES } from '../../constants'
import { useForm } from '../../Hooks/useForm'
import emailjs from '@emailjs/browser'

const templateId = 'template_tqty2mv'
const serviceId = 'service_pfpb65h'
const publicId = 's6HNn9FJmskIhvYFR'

const accordions = [
  {
    icon: <FontAwesomeIcon className="icon" icon={solid('circle-info')} />,
  },
  {
    icon: <FontAwesomeIcon className="icon" icon={solid('truck')} />,
  },
  {
    icon: <FontAwesomeIcon className="icon" icon={solid('plane-up')} />,
  },
]

const AIR_FREIGHT = [
  {
    title: 'Air Charter',
    content:
      'For urgent or oversized shipments outside standard airfreight parameters, we arrange dedicated charter flights — ensuring your cargo moves on your timeline, not the airline\'s.',
  },
  {
    title: 'On Board Courier',
    content:
      'When every hour counts, our on-board courier service guarantees hand-carried delivery worldwide. A dedicated courier accompanies your shipment door to door for maximum security and speed.',
  },
  {
    title: 'Express Air',
    content:
      'Our express air service is designed for time-definite shipments with strict deadlines. We guarantee day-specific delivery to destinations worldwide, backed by real-time tracking.',
  },
]

const OCEAN_FREIGHT = [
  {
    title: 'Tailored Solutions',
    content:
      'We craft flexible sea freight solutions around your unique requirements — whether FCL, LCL, or breakbulk — with frequent sailings, reliable transit times, and proactive exception management.',
  },
  {
    title: 'Global Network Coverage',
    content:
      'Our extensive network spans all major trade lanes and high-growth emerging markets, ensuring seamless support for your imports and exports wherever your business operates.',
  },
  {
    title: 'Cargo Visibility and Management',
    content:
      'Stay in full control with our advanced Track & Trace platform, real-time shipment updates, online documentation management, and comprehensive analytics and CO2 reporting.',
  },
]

const PACKAGING_AND_STORAGE = [
  {
    title: 'Food Grade Warehouse',
    content:
      'Our certified food-grade warehousing facilities meet the highest hygiene and compliance standards, ensuring safe storage and handling of perishable and temperature-sensitive inventory.',
  },
  {
    title: 'Distribution Centre',
    content:
      'We combine smart inventory management with strategic positioning to streamline your distribution operations — reducing lead times, cutting costs, and improving your customers\' experience.',
  },
  {
    title: 'Dedicated Warehouse',
    content:
      'For businesses requiring exclusive warehousing space and a fully dedicated logistics team, our dedicated warehouse solution offers maximum flexibility, security, and operational control.',
  },
]

const Service = () => {
  const { state } = useLocation()

  const [activeService, setActiveService] = useState(SERVICES[0])

  useEffect(() => {
    if (state?.service) {
      setActiveService(state.service)
    }
  }, [state])

  const handleServiceClick = (index) => () => {
    setActiveService(SERVICES[index])
  }

  const getServiceTitle = () => {
    switch (activeService) {
      case SERVICES[0]:
        return 'With 28 years of airfreight expertise, we deliver reliable, tailor-made air cargo solutions that keep your supply chain moving — no matter the complexity.'

      case SERVICES[1]:
        return 'Our deep experience in sea freight gives us the insight and network to deliver quality, cost-effective ocean freight services for your imports and exports worldwide.'

      case SERVICES[2]:
        return 'Sapphirre Bridge Logistics provides comprehensive cargo transportation solutions — from express customs clearance to specialised handling — ensuring your goods move efficiently across every stage of the journey.'

      case SERVICES[3]:
        return 'Sapphirre Bridge Logistics offers flexible ambient, temperature-controlled, and open yard storage solutions designed to meet the full range of your warehousing needs.'

      default:
        return ''
    }
  }

  const getServiceContent = () => {
    switch (activeService) {
      case SERVICES[0]:
        return `Air freight is fast-moving, dynamic, and constantly evolving. Global capacity and demand can shift at a moment's notice, which is why having a dependable partner is critical to maintaining the integrity of your supply chain. Whether your cargo travels in commercial aircraft holds or requires specialist freighter solutions, Sapphirre Bridge Logistics provides tailor-made air freight services built around your exact needs.\n\nDrawing on 28 years of industry experience, we offer a comprehensive suite of air freight products — from standard consolidations and express services to on-board courier and air charter. Our worldwide network means you have a knowledgeable local expert in every key market, just a call or click away.\n\nOur dedicated client onboarding teams ensure a smooth transition to our systems, and our real-time tracking platform keeps you fully informed on the status of every shipment. Contact us today to explore how Sapphirre Bridge Logistics can strengthen your air freight supply chain.`

      case SERVICES[1]:
        return `Whether you need full container loads, less-than-container groupage, or specialised breakbulk transport, Sapphirre Bridge Logistics handles every sea freight order with precision, care, and efficiency. We maintain fixed cargo space allocations on key trade lanes to guarantee capacity availability when you need it most.\n\nAll shipments are tracked end-to-end using our advanced digital logistics platform. Combined with rigorous quality standards and proactive communication, this gives our clients a high level of transparency and planning confidence throughout the journey.\n\nWe guide you through every aspect of the process — from customs regulations and import/export documentation to freight insurance and final delivery. Our team is ready to advise on the best routing options, freight solutions, transit times, and costs for your specific cargo requirements.`

      case SERVICES[2]:
        return `Our cargo handling facilities are equipped to manage a wide variety of shipment types — including standard, oversized, heavy-lift, and temperature-sensitive cargo. We offer dedicated refrigerated and frozen chambers to safely accommodate perishable goods, alongside high-capacity lifting equipment for over-dimensional freight.\n\nSapphirre Bridge Logistics acts as a certified agent for a broad portfolio of international air carriers, giving our clients access to the most suitable routing, scheduling, and pricing options for each shipment. All cargo is processed in full compliance with IATA regulations, ensuring seamless integration into the international transport network.\n\nOur experienced cargo team is on hand to advise on all aspects of your shipment — from origin to final destination — and we are always open to establishing long-term partnerships with businesses that value reliable, professional cargo management.`

      case SERVICES[3]:
        return `Sapphirre Bridge Logistics operates modern warehousing facilities offering extensive capacity across ambient, temperature-controlled, and open yard storage environments. Our strategically positioned sites provide excellent access to major logistics corridors, supporting efficient inbound and outbound distribution for businesses of all sizes.\n\nFrom food-grade certified storage and dedicated client facilities to tech-enabled inventory management and flexible short or long-term leasing arrangements, we provide warehousing solutions that adapt to your operational requirements. Our professional warehouse teams operate around the clock to ensure your inventory is secure, accurately managed, and ready for dispatch at all times.\n\nAs supply chain technology continues to evolve, Sapphirre Bridge Logistics is committed to integrating the latest innovations — including automated inventory systems and smart tracking solutions — to keep our clients ahead of the curve.`

      default:
        return ''
    }
  }

  const getAccordionData = () => {
    switch (activeService) {
      case SERVICES[0]:
        return accordions.map((acc, idx) => {
          acc.title = AIR_FREIGHT[idx].title
          acc.content = AIR_FREIGHT[idx].content

          return acc
        })

      case SERVICES[1]:
      case SERVICES[2]:
        return accordions.map((acc, idx) => {
          acc.title = OCEAN_FREIGHT[idx].title
          acc.content = OCEAN_FREIGHT[idx].content

          return acc
        })

      case SERVICES[3]:
        return accordions.map((acc, idx) => {
          acc.title = PACKAGING_AND_STORAGE[idx].title
          acc.content = PACKAGING_AND_STORAGE[idx].content

          return acc
        })

      default:
        return []
    }
  }

  const { form, reset, onChange } = useForm({
    name: '',
    address: '',
    email: '',
    msg: '',
    loading: false,
  })

  const handleChange = (e) => onChange(e.target.name, e.target.value)

  const handleSubmit = async (e) => {
    e.preventDefault()

    onChange('loading', true)

    try {
      await emailjs.send(serviceId, templateId, form, publicId)

      reset()
    } catch (error) {
      console.log('ERROR', error)
    }

    onChange('loading', false)
  }

  return (
    <div className="service">
      <div className="service__top">
        <h1>{activeService}</h1>
      </div>

      <div className="service__content">
        <section className="service-content">
          <div className="main-content">
            <SlideShow slideImages={[slide1, slide2, slide3]} />

            <h2 className="main-content__title">{getServiceTitle()}</h2>

            <div className="main-content__content">{getServiceContent()}</div>

            <h4>Our services: choose the right fit for your specific needs</h4>

            <div className="accordion-container">
              <AccordionGroup data={getAccordionData()} />
            </div>
          </div>

          <div className="side-menu">
            <div className="services-list">
              <ul>
                <li
                  className={
                    activeService === SERVICES[0] ? 'active' : undefined
                  }
                  onClick={handleServiceClick(0)}
                >
                  <div>Air Freight</div>
                </li>

                <li
                  className={
                    activeService === SERVICES[1] ? 'active' : undefined
                  }
                  onClick={handleServiceClick(1)}
                >
                  <div>Ocean Freight</div>
                </li>

                <li
                  className={
                    activeService === SERVICES[2] ? 'active' : undefined
                  }
                  onClick={handleServiceClick(2)}
                >
                  <div>Cargo Transportation</div>
                </li>

                <li
                  className={
                    activeService === SERVICES[3] ? 'active' : undefined
                  }
                  onClick={handleServiceClick(3)}
                >
                  <div>Packaging and Storage</div>
                </li>
              </ul>
            </div>

            <div className="contact-widget">
              <div className="location">
                <h3>LOCATION</h3>

                <div className="location__content">
                  <FontAwesomeIcon
                    className="icon"
                    icon={solid('location-dot')}
                  />

                  <div>
                    <span>Location:</span>

                    <span>GLOBAL FACILITY OFFICE</span>
                  </div>
                </div>
              </div>

              <div className="call-center">
                <h3>CALL CENTER</h3>

                <div className="call-center__content">
                  <FontAwesomeIcon className="icon" icon={solid('phone')} />

                  <div>
                    <span>Call Us:</span>

                    <span>+31 (970) 050-31758</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="contact-form">
              <h3>Get A Free Quote</h3>

              <form onSubmit={handleSubmit}>
                <Input
                  title="Full Name"
                  required
                  placeholder="Name"
                  value={form.name}
                  onChange={handleChange}
                  name="name"
                />

                <Input
                  title="Email"
                  type="email"
                  required
                  placeholder="Email"
                  value={form.email}
                  onChange={handleChange}
                  name="email"
                />

                <InputArea
                  title="Message"
                  required
                  rows={3}
                  value={form.msg}
                  onChange={handleChange}
                  name="msg"
                />

                <CustomButton
                  otherClassName="submit"
                  loading={form.loading}
                  label="SEND MESSAGE"
                />
              </form>
            </div>
          </div>
        </section>

        <UnbeatableSection />

        <OurServiceSection />
      </div>
    </div>
  )
}

export default Service
